import { Quote, Star, ArrowRight, Sparkles, TrendingUp, Users, Target } from 'lucide-react'

const testimonials = [
  {
    content:
      'Validea nos ayudó a validar nuestra idea en tiempo récord. En solo dos semanas pudimos confirmar que nuestro producto tenía demanda real.',
    author: {
      name: 'María González',
      role: 'CEO & Co-founder',
      company: 'TechStart',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      stats: {
        timeToValidation: '2 semanas',
        conversionRate: '+45%',
        costReduction: '-60%'
      }
    },
    highlights: [
      'Validación rápida',
      'Demanda confirmada',
      'Ahorro de recursos'
    ],
    color: 'from-blue-500 to-blue-600'
  },
  {
    content:
      'La integración fue súper simple y los datos que obtuvimos nos permitieron pivotear antes de gastar recursos en desarrollo.',
    author: {
      name: 'Juan Pérez',
      role: 'Product Manager',
      company: 'InnovateX',
      image: 'https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      stats: {
        timeToIntegration: '1 día',
        dataPoints: '10k+',
        insights: '15+'
      }
    },
    highlights: [
      'Integración rápida',
      'Datos valiosos',
      'Pivot exitoso'
    ],
    color: 'from-purple-500 to-purple-600'
  },
  {
    content:
      'Gracias a Validea pudimos identificar qué características eran las más valoradas por nuestros usuarios potenciales.',
    author: {
      name: 'Ana Martínez',
      role: 'Founder',
      company: 'GrowthLabs',
      image: 'https://images.unsplash.com/photo-1517841905240-472988babdf9?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      stats: {
        featuresValidated: '8',
        userFeedback: '500+',
        marketFit: '92%'
      }
    },
    highlights: [
      'Priorización clara',
      'Feedback directo',
      'Enfoque en valor'
    ],
    color: 'from-green-500 to-green-600'
  }
]

const statLabels: Record<string, string> = {
  timeToValidation: 'Tiempo de validación',
  conversionRate: 'Tasa de conversión',
  costReduction: 'Reducción de costos',
  timeToIntegration: 'Tiempo de integración',
  dataPoints: 'Puntos de datos',
  insights: 'Insights generados',
  featuresValidated: 'Características validadas',
  userFeedback: 'Feedback de usuarios',
  marketFit: 'Fit de mercado',
}

/**
 * Testimonials section component for the landing page
 * Displays user testimonials with detailed success stories
 */
export function Testimonials() {
  return (
    <section id="testimonials" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Testimonios</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Lo que dicen nuestros usuarios
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Descubrí cómo Validea está ayudando a emprendedores y empresas a validar sus ideas
          </p>
        </div>

        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {testimonials.map((testimonial, index) => (
            <div
              key={testimonial.author.name}
              className="group relative flex flex-col rounded-2xl border border-gray-200 bg-white p-8 shadow-sm transition-all hover:shadow-lg"
            >
              <div className="flex-1">
                <div className="flex items-center gap-x-4">
                  <div className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-r ${testimonial.color}`}>
                    <Quote className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold leading-7 text-gray-900">
                      {testimonial.author.name}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {testimonial.author.role} en {testimonial.author.company}
                    </p>
                  </div>
                </div>

                <blockquote className="mt-6 text-base leading-7 text-gray-600">
                  "{testimonial.content}"
                </blockquote>

                <div className="mt-6">
                  <div className="inline-flex items-center gap-x-2 rounded-full bg-primary-50 px-3 py-1 text-sm font-medium text-primary-600">
                    <Sparkles className="h-4 w-4" />
                    <span>Resultados clave</span>
                  </div>
                  <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                    {Object.entries(testimonial.author.stats).map(([key, value]) => (
                      <div key={key} className="flex flex-col">
                        <dt className="text-sm font-medium text-gray-600">{statLabels[key] || key}</dt>
                        <dd className="mt-1 text-lg font-semibold text-gray-900">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="mt-6">
                  <ul role="list" className="space-y-2">
                    {testimonial.highlights.map((highlight) => (
                      <li key={highlight} className="flex items-center gap-x-2 text-sm text-gray-600">
                        <Star className="h-4 w-4 text-primary-500" />
                        {highlight}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="mt-8 flex items-center gap-x-4">
                <img
                  className="h-10 w-10 rounded-full bg-gray-50"
                  src={testimonial.author.image}
                  alt={testimonial.author.name}
                />
                <div className="text-sm">
                  <a href="#contact" className="font-semibold text-primary-600 hover:text-primary-500">
                    Conviértete en un caso de éxito
                    <ArrowRight className="ml-1 inline-block h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 text-center">
          <a
            href="#contact"
            className="inline-flex items-center gap-x-2 rounded-full bg-primary-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
          >
            Unite a nuestros usuarios exitosos
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  )
} 